import { BusinessObjectBase } from './base';
import { studentSchema } from '@schemas/business-objects/student';
import type { AlternateStudent } from './alternate-student';
import type { Attendance } from './attendance';
import type { Organization } from './organization';
import type { Prescription } from './prescription';
import type { SelectedStudent } from './selected-student';
import type { SessionHistory } from './session-history';
import type { TestResult } from './test-result';
import type { User } from './user';
import type { CalendarDate, Email, GradeLevel, Id } from '../base-types';
import type { Curriculum } from './curriculum';
import type { NotAvailable } from './not-available';
import type { Resource } from './resource';
import type { Role } from './role';
import type { RoleAssignment } from './role-assignment';
import type { Session } from './session';
import type { SessionActivityItem } from './session-activity-item';
import type { Slot } from './slot';
import type { StudentAssignment } from './student-assignment';
import type { Term } from './term';
import type { TermTime } from './term-time';

export interface StudentData {
  readonly id: Id;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: Email;
  readonly dateOfBirth?: CalendarDate;
  readonly currentGradeLevel?: GradeLevel;
  readonly created?: Date;
  readonly organizationId: Id;
  readonly organization?: Organization;
  readonly appUserId: Id;
  readonly appUser?: User;
  readonly prescriptions?: Prescription[];
  readonly testResults?: TestResult[];
  readonly selectedStudents?: SelectedStudent[];
  readonly alternateStudents?: AlternateStudent[];
  readonly attendances?: Attendance[];
  readonly sessionHistories?: SessionHistory[];
}

export interface Student extends Readonly<StudentData> {}
type StudentCreateWithChildrenData = Partial<
  Omit<StudentData, 'prescriptions' | 'testResults' | 'attendances'>
> & {
  prescriptions?: Partial<Prescription>[];
  testResults?: Partial<TestResult>[];
  attendances?: Partial<Attendance>[];
};

export class Student extends BusinessObjectBase {
  static readonly entityName = 'Student' as const;
  static readonly plural = 'students';
  static schema = studentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Student[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Student(data));
  }

  static async findOne(filter?: any): Promise<Student | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Student(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Student> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Student(data);
  }

  static async create(data: Omit<StudentData, 'id'>): Promise<Student> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Student(result);
  }

  static async createMany(data: Omit<StudentData, 'id'>[]): Promise<Student[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Student(item));
  }

  static async createWithChildren(
    data: StudentCreateWithChildrenData,
  ): Promise<Student> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Student(result);
  }

  static async createManyWithChildren(
    data: StudentCreateWithChildrenData[],
  ): Promise<Student[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Student(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<StudentData>,
  ): Promise<Student> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Student(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async getLinkedResources(): Promise<any[]> {
    const url = `${BusinessObjectBase.baseUrl}/${Student.plural}/${(this as any).id}/get-linked-resources`;
    return BusinessObjectBase.get<any[]>(url);
  }
  static async forCurrentUser(): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/for-current-user`;
    return this.get<any>(url);
  }
  async findProposedAssignments(effectiveFrom: string): Promise<any[]> {
    const url = `${BusinessObjectBase.baseUrl}/${Student.plural}/${(this as any).id}/find-proposed-assignments`;
    return BusinessObjectBase.post<any[]>(url, { effectiveFrom });
  }
  async effectOutAssignmentsOnOrganizationChange(
    oldOrganizationId: number,
    effectiveFrom: string,
  ): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Student.plural}/${(this as any).id}/effect-out-assignments-on-organization-change`;
    return BusinessObjectBase.post<any>(url, {
      oldOrganizationId,
      effectiveFrom,
    });
  }
}
